import { Fragment, type ReactNode } from "react";
import { YStack, XStack, View, Text } from "tamagui";
import { RiPhoneLine } from "react-icons/ri";

import { v2 } from "@screen/Home/pages/GasShop/skin";
import type { BillDetail, BillMeterLine, BillOrderLine } from "../API/handleBillsAPI";
import {
  billPeriodLabel,
  billTotalRows,
  billTypeLabel,
  money,
  ymdSlash,
} from "./billDisplay";

const tabular = { fontVariantNumeric: "tabular-nums" as const };

/** 帳單區塊之間的分隔：虛線，像紙本帳單的裁切線 */
const Divider = ({ dashed = false }: { dashed?: boolean }) => (
  <View my="$3.5" btw={1} btc={dashed ? v2.cardBorder : v2.divider} style={{ borderTopStyle: dashed ? "dashed" : "solid" }} />
);

const Heading = ({ title, right }: { title: string; right?: ReactNode }) => (
  <XStack jc="space-between" ai="baseline" mb="$2">
    <Text fontSize={12} fontWeight="700" color={v2.label} letterSpacing={0.5}>
      {title}
    </Text>
    {right}
  </XStack>
);

const InfoLine = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <XStack jc="space-between" gap="$3" py={3}>
    <Text fontSize={12} color={v2.sub}>
      {label}
    </Text>
    <Text fontSize={12} fontWeight="600" color={v2.body} ta="right" fontFamily={mono ? "monospace" : undefined}>
      {value}
    </Text>
  </XStack>
);

const OrderLineRow = ({ line, isLast }: { line: BillOrderLine; isLast: boolean }) => (
  <YStack py="$2.5" gap="$1" bbw={isLast ? 0 : 1} bbc={v2.divider}>
    <XStack jc="space-between" ai="baseline">
      <Text fontSize={13} fontWeight="700" color={v2.title}>
        {line.delivery_date ? ymdSlash(line.delivery_date) : "訂單"}
      </Text>
      <Text fontSize={14} fontWeight="800" color={v2.title} style={tabular}>
        {money(line.amount)}
      </Text>
    </XStack>
    {line.items.map((item, idx) => (
      <XStack key={`${item.kind}-${idx}`} jc="space-between">
        <Text fontSize={12} color={v2.body}>
          {item.label} × {item.quantity}
        </Text>
        {item.unit_price !== null && (
          <Text fontSize={12} color={v2.sub} style={tabular}>
            單價 {money(item.unit_price)}
          </Text>
        )}
      </XStack>
    ))}
    {/* 同 admin 紙本帳單：含與不含樓層費的單都列（不含的是付給配送員的毛額），不另加說明文字 */}
    {line.floor_fee > 0 && (
      <Text fontSize={12} color={v2.body}>
        樓層費 {money(line.floor_fee)}
      </Text>
    )}
    {line.refunds.length > 0 && (
      <Text fontSize={12} color={v2.sub}>
        {line.refunds.map((r) => `${r.type} ${r.kilogram}KG`).join("、")}
      </Text>
    )}
  </YStack>
);

const MeterLineRow = ({ line, isLast }: { line: BillMeterLine; isLast: boolean }) => (
  <YStack py="$2.5" gap="$1" bbw={isLast ? 0 : 1} bbc={v2.divider}>
    <XStack jc="space-between" ai="baseline">
      <Text fontSize={13} fontWeight="700" color={v2.title}>
        {line.device_name}
      </Text>
      <Text fontSize={14} fontWeight="800" color={v2.title} style={tabular}>
        {money(line.amount)}
      </Text>
    </XStack>
    <Text fontSize={12} color={v2.body}>
      前次度數 {line.previous ? `${line.previous.degree}（${ymdSlash(line.previous.date)}）` : "—"}
    </Text>
    <Text fontSize={12} color={v2.body}>
      本次度數 {line.current ? `${line.current.degree}（${ymdSlash(line.current.date)}）` : "—"}
    </Text>
    <Text fontSize={12} color={v2.sub}>
      實用度數 {line.usage}
      {line.coefficient !== null ? ` · 係數 ${line.coefficient}` : ""}
      {line.unit_price !== null ? ` · 單價 ${money(line.unit_price)}` : ""}
    </Text>
  </YStack>
);

/**
 * 帳單本體：一張卡片排成紙本帳單的樣子（同瓦斯行印給客戶的那張）。
 * 抬頭（瓦斯行）→ 帳單資訊 → 明細 → 金額 → 發票 → 注意事項。收款紀錄另外一張卡（BillPaymentsCard）。
 * 繳費資訊、匯款帳戶不在這裡：那是客戶要「動手」的部分，放在頁面最上面。
 */
export default function BillDocumentCard({ bill }: { bill: BillDetail }) {
  const totalRows = billTotalRows(bill.totals);
  const grand = totalRows[totalRows.length - 1]!;
  const subRows = totalRows.slice(0, -1);
  const invoice = bill.invoice?.invoice_number ? bill.invoice : null;
  const hasNotes = !!bill.note || bill.notices.length > 0;

  return (
    <YStack
      className="gas-stagger"
      bg={v2.card}
      br={24}
      px={18}
      py={20}
      style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}>
      {/* 抬頭：瓦斯行 */}
      <YStack ai="center" gap="$1">
        <Text fontSize={16} fontWeight="800" color={v2.title} ta="center">
          {bill.supplier.name ?? "瓦斯行"}
        </Text>
        {bill.supplier.phone && (
          <a href={`tel:${bill.supplier.phone}`} style={{ textDecoration: "none" }}>
            <XStack ai="center" gap="$1">
              <RiPhoneLine size={12} color={v2.primary} />
              <Text fontSize={12} fontWeight="700" color={v2.primary}>
                {bill.supplier.phone}
              </Text>
            </XStack>
          </a>
        )}
        {bill.supplier.address && (
          <Text fontSize={11} color={v2.sub} ta="center">
            {bill.supplier.address}
          </Text>
        )}
        <Text fontSize={13} fontWeight="700" color={v2.body} mt="$2.5" letterSpacing={2}>
          {billTypeLabel(bill.bill_type)}
        </Text>
      </YStack>

      <Divider dashed />

      {/* 帳單資訊 */}
      <YStack>
        <InfoLine label="計費期間" value={billPeriodLabel(bill.period_start, bill.period_end)} />
        {bill.customer.tax_id_number && <InfoLine label="統一編號" value={bill.customer.tax_id_number} mono />}
        <InfoLine label="帳單編號" value={bill.unified_bill_id} mono />
      </YStack>

      {/* 明細 */}
      {bill.order_lines.length > 0 && (
        <>
          <Divider />
          <Heading
            title="訂單明細"
            right={
              <Text fontSize={11} color={v2.sub}>
                {bill.order_lines.length} 筆
              </Text>
            }
          />
          {bill.order_lines.map((line, i) => (
            <OrderLineRow key={line.order_id} line={line} isLast={i === bill.order_lines.length - 1} />
          ))}
        </>
      )}
      {bill.meter_lines.length > 0 && (
        <>
          <Divider />
          <Heading title="瓦斯表度數" />
          {bill.meter_lines.map((line, i) => (
            <MeterLineRow key={`${line.device_name}-${i}`} line={line} isLast={i === bill.meter_lines.length - 1} />
          ))}
        </>
      )}

      {/* 金額 */}
      <Divider />
      <YStack gap="$1.5">
        {subRows.map((row) => (
          <XStack key={row.label} jc="space-between" ai="baseline">
            <Text fontSize={13} color={v2.body}>
              {row.label}
            </Text>
            <Text fontSize={13} fontWeight="600" color={v2.title} style={tabular}>
              {row.value}
            </Text>
          </XStack>
        ))}
        <XStack jc="space-between" ai="baseline" mt="$1.5" pt="$2.5" btw={1} btc={v2.title}>
          <Text fontSize={14} fontWeight="800" color={v2.title}>
            {grand.label}
          </Text>
          <Text fontSize={20} fontWeight="800" color={v2.title} style={tabular}>
            {grand.value}
          </Text>
        </XStack>
      </YStack>

      {/* 發票 */}
      {invoice && (
        <>
          <Divider />
          <Heading title="發票" />
          <InfoLine label="發票號碼" value={invoice.invoice_number!} mono />
          {invoice.invoice_time && <InfoLine label="開立時間" value={ymdSlash(invoice.invoice_time.slice(0, 10))} />}
          {invoice.random_number && <InfoLine label="隨機碼" value={invoice.random_number} mono />}
          {invoice.has_allowance && <InfoLine label="折讓" value="已開立折讓" />}
        </>
      )}

      {/* 備註、注意事項 */}
      {hasNotes && (
        <>
          <Divider />
          <Heading title="注意事項" />
          <YStack gap="$1.5">
            {[bill.note, ...bill.notices].filter(Boolean).map((text, i) => (
              <Fragment key={i}>
                <Text fontSize={12} color={v2.body} lineHeight={18} style={{ whiteSpace: "pre-wrap" }}>
                  {text}
                </Text>
              </Fragment>
            ))}
          </YStack>
        </>
      )}
    </YStack>
  );
}
